import React from 'react';
import { css } from '@emotion/react';
import { Link } from 'gatsby';
import { useCart } from '../context/cart-context';

const overlayStyle = (isOpen: boolean) => css`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  z-index: 100;
  opacity: ${isOpen ? 1 : 0};
  visibility: ${isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const drawerStyle = (isOpen: boolean) => css`
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100%;
  background: white;
  z-index: 101;
  transform: translateX(${isOpen ? '0' : '100%'});
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const drawerHeaderStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
`;

const drawerTitleStyle = css`
  font-weight: bold;
  font-size: 0.9rem;
  letter-spacing: 0.1em;
`;

const closeButtonStyle = css`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  color: black;
  -webkit-appearance: none;
`;

const drawerBodyStyle = css`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
`;

const emptyStyle = css`
  text-align: center;
  color: #999;
  font-size: 0.9rem;
  margin-top: 40px;
`;

const cartItemStyle = css`
  display: flex;
  gap: 15px;
  padding: 15px 0;
  border-bottom: 1px solid #f0f0f0;
`;

const cartItemImageStyle = (colorCode: string) => css`
  width: 80px;
  height: 80px;
  background: #f5f5f5;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  div {
    width: 60%;
    height: 60%;
    background: ${colorCode};
    border: ${colorCode === '#FFFFFF' || colorCode === '#F5F5DC' ? '1px solid #e0e0e0' : 'none'};
  }
`;

const cartItemInfoStyle = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const cartItemTitleStyle = css`
  font-weight: bold;
  font-size: 0.8rem;
  letter-spacing: 0.02em;
  line-height: 1.3;

  a {
    color: black;
    text-decoration: none;

    &:hover {
      color: #348dea;
    }
  }
`;

const cartItemVariantStyle = css`
  font-size: 0.75rem;
  color: #666;
`;

const cartItemPriceStyle = css`
  font-size: 0.85rem;
  font-weight: bold;
`;

const cartItemActionsStyle = css`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
`;

const itemQuantityStyle = css`
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid #ccc;
`;

const itemQuantityButtonStyle = css`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  color: black;
  -webkit-appearance: none;

  &:hover {
    background: #f5f5f5;
  }
`;

const itemQuantityValueStyle = css`
  width: 32px;
  text-align: center;
  font-size: 0.8rem;
  font-weight: bold;
  border-left: 1px solid #ccc;
  border-right: 1px solid #ccc;
  height: 28px;
  line-height: 28px;
`;

const removeButtonStyle = css`
  background: none;
  border: none;
  font-size: 0.75rem;
  color: #999;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: black;
  }
`;

const drawerFooterStyle = css`
  padding: 20px;
  border-top: 1px solid #e0e0e0;
`;

const totalRowStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const totalLabelStyle = css`
  font-weight: bold;
  font-size: 0.9rem;
  letter-spacing: 0.05em;
`;

const totalPriceStyle = css`
  font-weight: bold;
  font-size: 1.1rem;
`;

const taxNoteStyle = css`
  font-size: 0.7rem;
  font-weight: normal;
  margin-left: 6px;
`;

const checkoutButtonStyle = css`
  width: 100%;
  height: 50px;
  background: black;
  color: white;
  border: none;
  font-weight: bold;
  font-size: 0.9rem;
  letter-spacing: 0.1em;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const formatPrice = (price: number) => {
  return `¥${price.toLocaleString()}`;
};

const cartItemRealImageStyle = css`
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  object-fit: cover;
  background: #f5f5f5;
`;

const CartDrawer: React.FC = () => {
  const { items, isOpen, closeCart, removeItem, updateQuantity, totalPrice, checkoutUrl, isShopifyConnected } = useCart();

  const handleCheckout = () => {
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
    }
  };

  return (
    <>
      <div css={overlayStyle(isOpen)} onClick={closeCart} />
      <div css={drawerStyle(isOpen)}>
        <div css={drawerHeaderStyle}>
          <div css={drawerTitleStyle}>CART</div>
          <button css={closeButtonStyle} onClick={closeCart}>×</button>
        </div>

        <div css={drawerBodyStyle}>
          {items.length === 0 ? (
            <div css={emptyStyle}>Your cart is empty</div>
          ) : (
            items.map(item => (
              <div key={item.variantId} css={cartItemStyle}>
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt={item.productTitle} css={cartItemRealImageStyle} />
                ) : (
                  <div css={cartItemImageStyle(item.colorCode)}>
                    <div />
                  </div>
                )}
                <div css={cartItemInfoStyle}>
                  <div css={cartItemTitleStyle}>
                    <Link to={`/shop/${item.handle}`} onClick={closeCart}>
                      {item.productTitle}
                    </Link>
                  </div>
                  <div css={cartItemVariantStyle}>{item.variantTitle}</div>
                  <div css={cartItemPriceStyle}>{formatPrice(item.price)}</div>
                  <div css={cartItemActionsStyle}>
                    <div css={itemQuantityStyle}>
                      <button
                        css={itemQuantityButtonStyle}
                        onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                      >
                        −
                      </button>
                      <div css={itemQuantityValueStyle}>{item.quantity}</div>
                      <button
                        css={itemQuantityButtonStyle}
                        onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                      >
                        +
                      </button>
                    </div>
                    <button
                      css={removeButtonStyle}
                      onClick={() => removeItem(item.variantId)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div css={drawerFooterStyle}>
            <div css={totalRowStyle}>
              <div css={totalLabelStyle}>SUBTOTAL</div>
              <div css={totalPriceStyle}>
                {formatPrice(totalPrice)}
                <span css={taxNoteStyle}>TAX INCLUDED</span>
              </div>
            </div>
            <button
              css={checkoutButtonStyle}
              onClick={handleCheckout}
              disabled={isShopifyConnected && !checkoutUrl}
            >
              {isShopifyConnected ? 'CHECKOUT' : 'CHECKOUT (COMING SOON)'}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
